import { useMutation, useQuery } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { CurrentUserFirmsProps } from '@deltek/specification-client/core/Models/currentUserInfo'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { RecentlyViewedProjectsResponse } from 'Models/project'
import { RecentlyViewedProjectsQuery } from 'api/graphQL/queries/project.queries'
import React, { useEffect } from 'react'
import ProjectGridItem from './ProjectGridItem'
import RecentProjectsLoadState from './RecentProjectsLoadState'

const useStyles = makeStyles((theme: Theme) => ({
    recentProjectsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gridGap: 4,
    },
    grayText: {
        ...theme.typography.h1,
        color: theme.palette.grey[700],
    },
    centerContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 130,
    },
}))

export interface RecentProjectsWidgetProps {
    handleProjectFavoriteStateChange?: (
        favorite: boolean,
        projectId: string
    ) => void
    reloadRecentProjects: boolean
    triggerRecentProjectsReload: React.Dispatch<React.SetStateAction<boolean>>
    openRemoveProjectConfirmation: (projectId: string | undefined) => void
    isAdmin?: boolean
    activeFirmInfo: CurrentUserFirmsProps
}
const RecentProjectsWidget: React.FC<RecentProjectsWidgetProps> = ({
    handleProjectFavoriteStateChange,
    reloadRecentProjects,
    triggerRecentProjectsReload,
    openRemoveProjectConfirmation,
    isAdmin,
    activeFirmInfo,
}) => {
    const classes = useStyles()

    const {
        data: recentlyViewedProjectsData,
        error: recentlyViewedProjectsError,
        loading: recentlyViewedProjectsLoading,
        refetch: refetchRecentlyViewed,
    } = useQuery<RecentlyViewedProjectsResponse>(RecentlyViewedProjectsQuery)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    useEffect(() => {
        if (reloadRecentProjects) {
            triggerRecentProjectsReload(!reloadRecentProjects)
            refetchRecentlyViewed()
        }
    }, [
        reloadRecentProjects,
        refetchRecentlyViewed,
        triggerRecentProjectsReload,
    ])

    useEffect(() => {
        if (recentlyViewedProjectsError) {
            showGlobalSnackbar({
                variables: {
                    id:
                        'ProjectsHomeRecentProjectsWidget_RecentlyViewedProjects',
                    variant: 'error',
                    message: 'Error loading recent projects.',
                },
            })
        }
    }, [showGlobalSnackbar, recentlyViewedProjectsError])

    if (recentlyViewedProjectsLoading) {
        return <RecentProjectsLoadState />
    }

    return (recentlyViewedProjectsData?.getRecentlyViewedProjects.length ?? 0) >
        0 ? (
        <div className={classes.recentProjectsContainer}>
            {recentlyViewedProjectsData!.getRecentlyViewedProjects.map(
                (project, index) => {
                    return (
                        <div key={`projectItem-${project.id}`}>
                            <ProjectGridItem
                                key={`projectItem-${index}`}
                                isLoading={false}
                                project={project}
                                handleFavoriteChange={
                                    handleProjectFavoriteStateChange
                                }
                                isAdmin={isAdmin}
                                activeFirmInfo={activeFirmInfo}
                                openRemoveDialog={openRemoveProjectConfirmation}
                            />
                        </div>
                    )
                }
            )}
        </div>
    ) : (
        <div
            className={classes.centerContainer}
            data-testid="noRecentContainer"
        >
            <Typography className={classes.grayText}>
                No Recently Opened Projects
            </Typography>
        </div>
    )
}

export default RecentProjectsWidget
